import { fetchWithTimeout } from './deco-http.js';
import {
  fetchCartState,
  getMaxedFurnitureGroupSet,
  getMaxedFurnitureSet,
  getMaxedFurnitureTypeGroupSet,
  getPackRulesConfig,
  getFurnitureTypeLimitLabel,
  getSelectedSkuSet,
  mapSkuToFurniture,
  mapSkuToFurnitureGroup,
  normalizeFurnitureGroupKey,
  normalizeSku,
  sortCartItemsBySelectionOrder,
  validatePackSelection,
} from '@theme/pack-rules';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import {
  clearDecoAccountSession,
  DECO_ACCOUNT_CHANGED_EVENT,
  DECO_ACCOUNT_SESSION_KEYS,
  dispatchDecoAccountChanged,
  getDecoAccountSessionToken,
  writeDecoAccountSession,
} from '@theme/deco-account-session';
import {
  forgetSystemSelection,
  getSystemSelection,
} from '@theme/system-selection';
const DEFAULT_SUBSCRIPTION_TYPE = 'indefinite';
const CHECKOUT_BASE_URL = 'https://pagos.decorental.com.ar/checkout/start';
const PACK_QUOTE_ENDPOINTS = [
  'https://api.decorental.com.ar/v1/packs/quote',
  'https://api.decorental.com.ar/v1/packs/calculate',
  'https://api.decorental.com.ar/v1/packs/quotation',
];
const MODAL_ID = 'pack-checkout-modal';
const MODAL_OPEN_CLASS = 'pack-checkout-modal-open';
const PACK_CHECKOUT_SUBMITTED_MESSAGE = 'deco:pack-checkout:submitted';
const PACK_CHECKOUT_ALLOWED_ORIGINS = new Set([
  'https://pagos.decorental.com.ar',
  'https://api.decorental.com.ar',
]);
const QUOTE_LOADING_TEXT = 'Calculando...';
const ACCOUNT_REQUIRED_FIELDS = ['nombre', 'email', 'telefono', 'cuil', 'direccion', 'direccion_numero', 'codigo_postal', 'ciudad'];
const ACCOUNT_SUBSCRIPTIONS_TAB = 'suscripciones';
const quoteCache = new Map();
const quoteRequests = new Map();
let latestPreviewRequestId = 0;
let latestAvailabilityRequestId = 0;
let latestAccountRefreshRequestId = 0;

function trackCheckoutEvent(eventName, params = {}) {
  window.DecoAnalytics?.track?.(eventName, {
    source: 'CHECKOUT',
    ...params,
  });
}

function createPackCheckoutModal() {
  const existingModal = document.getElementById(MODAL_ID);
  if (existingModal instanceof HTMLDivElement) {
    return existingModal;
  }

  const modal = document.createElement('div');
  modal.id = MODAL_ID;
  modal.className = 'pack-checkout-modal';
  modal.setAttribute('aria-hidden', 'true');

  modal.innerHTML = `
    <div class="pack-checkout-modal__backdrop" data-pack-modal-close></div>
    <section class="pack-checkout-modal__dialog" role="dialog" aria-modal="true" aria-label="Checkout de pack">
      <button type="button" class="pack-checkout-modal__close" data-pack-modal-close aria-label="Cerrar checkout">×</button>
      <div class="pack-checkout-modal__iframe-wrap">
        <iframe
          class="pack-checkout-modal__iframe"
          title="Checkout DecoRental"
          loading="eager"
          allow="payment *"
          referrerpolicy="strict-origin-when-cross-origin"
        ></iframe>
      </div>
    </section>
  `;

  modal.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-pack-modal-close]')) {
      closePackCheckoutModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
      closePackCheckoutModal();
    }
  });

  document.body.appendChild(modal);
  return modal;
}

function getModalIframe() {
  const modal = createPackCheckoutModal();
  return modal.querySelector('.pack-checkout-modal__iframe');
}

function openPackCheckoutModal(url) {
  const modal = createPackCheckoutModal();
  const iframe = getModalIframe();

  if (!(iframe instanceof HTMLIFrameElement)) {
    throw new Error('No fue posible preparar el checkout en esta pantalla.');
  }

  iframe.src = url;
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add(MODAL_OPEN_CLASS);
}

async function closeCartDrawerIfOpen() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!(cartDrawer instanceof HTMLElement)) return;

  if (typeof cartDrawer.closeDialog === 'function') {
    await cartDrawer.closeDialog();
    return;
  }

  if (typeof cartDrawer.close === 'function') {
    cartDrawer.close();
    await new Promise((resolve) => setTimeout(resolve, 260));
    return;
  }

  cartDrawer.removeAttribute('open');
}

async function closeDecoAccountPanelIfOpen() {
  const popover = document.getElementById('account-popover');
  if (popover instanceof HTMLElement && typeof popover.hidePopover === 'function') {
    try {
      popover.hidePopover();
    } catch (_error) {
      // El popover puede no estar abierto.
    }
  }

  const accountDrawer = document.querySelector('dialog-component.account-drawer');
  if (accountDrawer instanceof HTMLElement && typeof accountDrawer.closeDialog === 'function') {
    await accountDrawer.closeDialog();
  }
}

function openCartDrawerIfAvailable() {
  const cartDrawer = document.querySelector('cart-drawer-component');
  if (!(cartDrawer instanceof HTMLElement)) return false;

  if (typeof cartDrawer.open === 'function') {
    cartDrawer.open();
    return true;
  }

  if (typeof cartDrawer.showDialog === 'function') {
    cartDrawer.showDialog();
    return true;
  }

  return false;
}

async function handleDecoAccountAfterComplete(event) {
  if (event.detail?.action !== 'open-cart-drawer') return;

  await closeDecoAccountPanelIfOpen();
  refreshAccountAwareCheckoutButtons();
  openCartDrawerIfAvailable();
}

async function openDecoAccountPanel(button) {
  window.dispatchEvent(new CustomEvent('deco-account:return-to', {
    detail: { afterComplete: 'open-cart-drawer' },
  }));

  await closeCartDrawerIfOpen();
  window.scrollTo(0, 0);
  await new Promise((resolve) => requestAnimationFrame(resolve));

  const prefersDrawer = window.matchMedia('(max-width: 749px)').matches;
  const accountDrawer = document.querySelector('dialog-component.account-drawer');
  if (prefersDrawer && accountDrawer && typeof accountDrawer.showDialog === 'function') {
    accountDrawer.showDialog();
    return;
  }

  const popover = document.getElementById('account-popover');
  if (popover instanceof HTMLElement && typeof popover.showPopover === 'function') {
    try {
      popover.showPopover();
      return;
    } catch (_error) {
      try {
        popover.hidePopover?.();
        popover.showPopover();
        return;
      } catch (_secondError) {
        window.location.href = getAccountRedirectUrl(button);
      }
    }
  }

  window.location.href = getAccountRedirectUrl(button);
}

function closePackCheckoutModal() {
  const modal = document.getElementById(MODAL_ID);
  if (!(modal instanceof HTMLDivElement)) return;

  const iframe = modal.querySelector('.pack-checkout-modal__iframe');
  if (iframe instanceof HTMLIFrameElement) {
    iframe.src = 'about:blank';
  }

  modal.setAttribute('aria-hidden', 'true');
  document.body.classList.remove(MODAL_OPEN_CLASS);
}

function resetPackQuoteStateAfterCartClear() {
  quoteCache.clear();
  lastRenderedQuote = null;
  updatePackQuotePreview();
  updatePackAvailabilityUi();
  refreshAccountAwareCheckoutButtons();
}

function clearSystemCartAfterPackCheckout() {
  const cart = forgetSystemSelection({ notify: true, source: 'CHECKOUT' });
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'pack-checkout', {
      source: 'pack-checkout',
      action: 'clear',
      itemCount: 0,
      itemCountIsTotal: true,
    })
  );
  return cart;
}

async function refreshStoredAccountAfterPackCheckout(accountButton) {
  if (!(accountButton instanceof HTMLButtonElement) || !getStoredAccountToken()) return;
  await fetchCurrentAccountProfile(accountButton, { notify: false });
  dispatchDecoAccountChanged({ reason: 'checkout-completed' });
}

function getAccountSubscriptionsUrl(button) {
  const profilePath = button instanceof HTMLButtonElement
    ? (button.dataset.decoAccountProfilePath || '/pages/mi-cuenta').trim() || '/pages/mi-cuenta'
    : '/pages/mi-cuenta';
  const url = new URL(profilePath, window.location.origin);
  url.searchParams.set('tab', ACCOUNT_SUBSCRIPTIONS_TAB);
  url.hash = ACCOUNT_SUBSCRIPTIONS_TAB;
  return `${url.pathname}${url.search}${url.hash}`;
}

async function handlePackCheckoutMessage(event) {
  if (!PACK_CHECKOUT_ALLOWED_ORIGINS.has(event.origin)) return;
  if (event.data?.type !== PACK_CHECKOUT_SUBMITTED_MESSAGE) return;

  const accountButton = document.querySelector('[data-pack-quote-button]');
  trackCheckoutEvent('checkout_completed', {
    selection_id: event.data?.selection_id || null,
    metadata: { result: 'submitted' },
  });
  closePackCheckoutModal();
  clearSystemCartAfterPackCheckout();
  resetPackQuoteStateAfterCartClear();

  try {
    await refreshStoredAccountAfterPackCheckout(accountButton);
  } catch (error) {
    console.warn('[pack-quote] No se pudo refrescar Mi cuenta despues de iniciar Mercado Pago.', error);
  }

  window.location.href = getAccountSubscriptionsUrl(accountButton);
}

async function fetchCartItems() {
  const [cart, config] = await Promise.all([fetchCartState(), getPackRulesConfig()]);
  const sortedCartItems = sortCartItemsBySelectionOrder(cart.items || [], config);
  const items = sortedCartItems.map((item) => ({
    sku: (item.sku || '').trim(),
    qty: item.quantity,
  }));

  if (items.length === 0) {
    throw new Error('Mi selección está vacía.');
  }

  if (items.some((item) => !item.sku)) {
    throw new Error('Hay productos sin SKU. No se puede continuar.');
  }

  return items;
}

async function validateCartWithPackRules(items) {
  const config = await getPackRulesConfig();
  return {
    config,
    validation: validatePackSelection(items, config),
  };
}

function toBase64UrlSafe(value) {
  const bytes = new TextEncoder().encode(value);
  let binary = '';

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function buildCheckoutUrl(items, accountProfile = null, selectionId = '') {
  const payload = {
    items,
    subscription_type: DEFAULT_SUBSCRIPTION_TYPE,
  };

  if (accountProfile) {
    payload.cliente = getCheckoutAccountPayload(accountProfile);
  }
  if (selectionId) {
    payload.selection_id = selectionId;
  }

  const payloadBase64UrlSafe = toBase64UrlSafe(JSON.stringify(payload));
  return `${CHECKOUT_BASE_URL}?p=${payloadBase64UrlSafe}`;
}

function getErrorElement(button) {
  return button.closest('.cart__ctas')?.querySelector('[data-pack-quote-error]') ?? null;
}

function saveStoredAccountProfile(profile) {
  if (!profile || typeof profile !== 'object') return;
  writeDecoAccountSession({ profile: normalizeAccountProfile(profile) });
}

function saveStoredAccountPayload(payload) {
  saveStoredAccountProfile(payload);
  if (Array.isArray(payload?.subscriptions)) {
    writeDecoAccountSession({ subscriptions: payload.subscriptions });
  }
}

function clearStoredAccount(reason = 'invalid-session') {
  clearDecoAccountSession({ reason });
}

function getStoredAccountToken() {
  return getDecoAccountSessionToken();
}

function updateCartLoginPromptVisibility() {
  const shouldHidePrompt = Boolean(getStoredAccountToken());
  document.querySelectorAll('[data-deco-cart-login-prompt]').forEach((element) => {
    if (element instanceof HTMLElement) {
      element.hidden = shouldHidePrompt;
    }
  });
}

function normalizeAccountProfile(payload) {
  const source = payload?.cliente || payload?.profile || payload?.user || payload || {};
  return {
    cliente_id: payload?.cliente_id ?? source.cliente_id ?? source.id ?? null,
    nombre: source.nombre ?? source.given_name ?? '',
    apellido: source.apellido ?? source.family_name ?? '',
    email: source.email ?? '',
    telefono: source.telefono ?? '',
    cuil: source.cuil ?? '',
    direccion: source.direccion ?? '',
    direccion_numero: source.direccion_numero ?? '',
    direccion_piso: source.direccion_piso ?? '',
    direccion_dpto: source.direccion_dpto ?? '',
    codigo_postal: source.codigo_postal ?? '',
    ciudad: source.ciudad ?? '',
  };
}

function getMissingAccountFields(profile) {
  const normalized = normalizeAccountProfile(profile);
  return ACCOUNT_REQUIRED_FIELDS.filter((field) => !String(normalized[field] || '').trim());
}

function isAccountProfileComplete(profile) {
  return getMissingAccountFields(profile).length === 0;
}

function getAccountButtonState() {
  if (!getStoredAccountToken()) return 'signedOut';
  return 'ready';
}

function unlockButtonForAccountAction(button) {
  if (button.dataset.decoAccountUnlocked !== 'true') {
    button.dataset.decoAccountOriginalDisabled = button.disabled ? 'true' : 'false';
    button.dataset.decoAccountOriginalAriaDisabled = button.getAttribute('aria-disabled') || '';
    button.dataset.decoAccountOriginalPackClass = button.classList.contains('cart__checkout-button--pack-disabled') ? 'true' : 'false';
  }

  button.dataset.decoAccountUnlocked = 'true';
  button.disabled = false;
  button.setAttribute('aria-disabled', 'false');
  button.classList.remove('cart__checkout-button--pack-disabled');
}

function restoreButtonAfterAccountAction(button) {
  if (button.dataset.decoAccountUnlocked !== 'true') return;

  button.disabled = button.dataset.decoAccountOriginalDisabled === 'true';
  if (button.dataset.decoAccountOriginalAriaDisabled) {
    button.setAttribute('aria-disabled', button.dataset.decoAccountOriginalAriaDisabled);
  } else {
    button.removeAttribute('aria-disabled');
  }
  button.classList.toggle(
    'cart__checkout-button--pack-disabled',
    button.dataset.decoAccountOriginalPackClass === 'true'
  );

  delete button.dataset.decoAccountUnlocked;
  delete button.dataset.decoAccountOriginalDisabled;
  delete button.dataset.decoAccountOriginalAriaDisabled;
  delete button.dataset.decoAccountOriginalPackClass;
}

function getCheckoutAccountPayload(profile) {
  const normalized = normalizeAccountProfile(profile);
  return {
    cliente_id: normalized.cliente_id,
    nombre: normalized.nombre,
    apellido: normalized.apellido,
    email: normalized.email,
    telefono: normalized.telefono,
    cuil: normalized.cuil,
    direccion: normalized.direccion,
    direccion_numero: normalized.direccion_numero,
    direccion_piso: normalized.direccion_piso,
    direccion_dpto: normalized.direccion_dpto,
    codigo_postal: normalized.codigo_postal,
    ciudad: normalized.ciudad,
  };
}

function getAccountEndpoint(button, path) {
  const apiBaseUrl = (button.dataset.decoAccountApiBaseUrl || '/apps/deco-account').trim().replace(/\/+$/, '');
  return `${apiBaseUrl}${path}`;
}

async function fetchCurrentAccountProfile(button, { notify = true, promptTerms = false } = {}) {
  const token = getStoredAccountToken();
  if (!token) return null;

  const useCredentials = !/^https?:\/\//i.test((button.dataset.decoAccountApiBaseUrl || '').trim());
  const response = await fetchWithTimeout(getAccountEndpoint(button, '/me'), {
    credentials: useCredentials ? 'include' : 'omit',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 401 || response.status === 403) {
    clearStoredAccount('invalid-session');
    return null;
  }

  if (!response.ok) {
    throw new Error('No se pudo validar la cuenta.');
  }

  const data = await response.json();
  if (data?.terms?.pending === true) {
    if (!promptTerms) {
      const pendingProfile = normalizeAccountProfile(data);
      saveStoredAccountPayload(data);
      return pendingProfile;
    }

    if (window.DecoTerms?.ensureAccepted) {
      await window.DecoTerms.ensureAccepted((button.dataset.decoAccountApiBaseUrl || '/apps/deco-account').trim());
      return fetchCurrentAccountProfile(button, { notify, promptTerms: false });
    }

    throw new Error('Aceptá los términos y condiciones para continuar.');
  }

  const profile = normalizeAccountProfile(data);
  saveStoredAccountPayload(data);
  if (notify) {
    dispatchDecoAccountChanged({ reason: 'profile-refreshed' });
  }
  return profile;
}

async function getCheckoutAccountProfile(button) {
  if (!getStoredAccountToken()) return null;

  try {
    return await fetchCurrentAccountProfile(button, { promptTerms: true });
  } catch (error) {
    throw error;
  }
}

function getAccountRedirectUrl(button) {
  const profilePath = (button.dataset.decoAccountProfilePath || '/pages/mi-cuenta').trim() || '/pages/mi-cuenta';
  const url = new URL(profilePath, window.location.origin);
  url.searchParams.set('return_to', '/cart');
  return `${url.pathname}${url.search}${url.hash}`;
}

function redirectToAccount(button) {
  openDecoAccountPanel(button);
}

function setAccountButtonLabel(button, state = getAccountButtonState()) {
  if (!(button instanceof HTMLButtonElement)) return;
  if (button.dataset.packQuoteProcessing === 'true') return;

  button.dataset.decoAccountState = state;

  if (state === 'ready') {
    restoreButtonAfterAccountAction(button);
    button.textContent = 'Continuar';
  } else if (state === 'validating') {
    unlockButtonForAccountAction(button);
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.textContent = 'Validando...';
  } else if (state === 'error') {
    restoreButtonAfterAccountAction(button);
    button.textContent = 'Continuar';
  } else {
    unlockButtonForAccountAction(button);
    button.textContent = 'Inicia Sesión';
  }
}

function updateAccountAwareCheckoutButtons(state = getAccountButtonState()) {
  updateCartLoginPromptVisibility();
  document.querySelectorAll('[data-pack-quote-button]').forEach((button) => {
    if (button instanceof HTMLButtonElement) {
      setAccountButtonLabel(button, state);
    }
  });
}

async function refreshAccountAwareCheckoutButtons() {
  const requestId = ++latestAccountRefreshRequestId;
  if (!getStoredAccountToken()) {
    updateAccountAwareCheckoutButtons('signedOut');
    return;
  }
  updateAccountAwareCheckoutButtons('validating');

  const button = document.querySelector('[data-pack-quote-button]');
  if (!(button instanceof HTMLButtonElement)) return;

  try {
    await fetchCurrentAccountProfile(button, { notify: false });
  } catch (error) {
    console.warn('[pack-quote] No se pudo refrescar la cuenta Deco.', error);
    if (requestId === latestAccountRefreshRequestId && getStoredAccountToken()) {
      updateAccountAwareCheckoutButtons('error');
    }
    return;
  }

  if (requestId !== latestAccountRefreshRequestId) return;
  updateAccountAwareCheckoutButtons();
}

function addedNodesContainSelector(mutations, selector) {
  return mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
    if (!(node instanceof Element)) return false;
    return node.matches(selector) || Boolean(node.querySelector(selector));
  }));
}

function mutationsResetAccountUi(mutations) {
  return mutations.some((mutation) => {
    const target = mutation.target;
    if (!(target instanceof HTMLElement)) return false;

    const button = target.matches('[data-pack-quote-button]')
      ? target
      : target.closest('[data-pack-quote-button]');
    return button instanceof HTMLButtonElement && !button.dataset.decoAccountState;
  });
}

function mutationsResetPackQuoteUi(mutations) {
  return mutations.some((mutation) => {
    const target = mutation.target;
    if (!(target instanceof HTMLElement)) return false;

    const preview = target.matches('[data-pack-quote-preview]')
      ? target
      : target.closest('[data-pack-quote-preview]');
    return preview instanceof HTMLElement && !preview.dataset.packQuoteRendered;
  });
}

function watchCheckoutButtonMounts() {
  const observer = new MutationObserver((mutations) => {
    const hasAccountUi = mutationsResetAccountUi(mutations) || addedNodesContainSelector(
      mutations,
      '[data-pack-quote-button], [data-deco-cart-login-prompt]'
    );
    const hasPackSummaryUi = mutationsResetPackQuoteUi(mutations) || addedNodesContainSelector(
      mutations,
      '[data-pack-quote-preview]'
    );

    if (hasAccountUi) {
      refreshAccountAwareCheckoutButtons();
    }

    if (hasPackSummaryUi) {
      updatePackQuotePreview();
    }
  });

  // Shopify puede reemplazar el body al navegar y puede conservar el botón al
  // hacer morph de una sección. El documentElement sobrevive a ambos casos.
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-deco-account-state', 'data-pack-quote-rendered'],
    childList: true,
    subtree: true,
  });
}

function normalizeForMatch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' y ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeHandle(value) {
  return normalizeForMatch(value).replace(/\s+/g, '-');
}

function cssEscape(value) {
  if (window.CSS?.escape) {
    return window.CSS.escape(value);
  }

  return String(value || '').replace(/["\\]/g, '\\$&');
}

function textMatchesFurniture(value, furnitureLabel) {
  const normalizedValue = normalizeFurnitureGroupKey(value);
  const normalizedFurniture = normalizeFurnitureGroupKey(furnitureLabel);

  if (!normalizedValue || !normalizedFurniture) return false;

  return (
    normalizedValue === normalizedFurniture ||
    normalizedValue === `${normalizedFurniture}s` ||
    `${normalizedValue}s` === normalizedFurniture ||
    normalizedValue.includes(normalizedFurniture) ||
    normalizedFurniture.includes(normalizedValue)
  );
}

function getFurnitureLabels(furnitureKeys, config) {
  return Array.from(furnitureKeys)
    .map((muebleKey) => config.rulesByMueble.get(muebleKey)?.mueble || muebleKey)
    .filter(Boolean);
}

function getDisabledCategoryLabels(maxedFurniture, maxedFurnitureTypeGroups, config) {
  return Array.from(
    new Set([
      ...getFurnitureLabels(maxedFurniture, config),
      ...Array.from(maxedFurnitureTypeGroups).map((groupKey) => getFurnitureTypeLimitLabel(groupKey, config)),
    ])
  ).filter(Boolean);
}

function setButtonDisabledByPackRules(button, disabled, label) {
  if (!(button instanceof HTMLButtonElement)) return;

  if (disabled && button.dataset.packDisabledByRules !== 'true') {
    button.dataset.packOriginalDisabled = button.disabled ? 'true' : 'false';
  }

  if (disabled) {
    button.dataset.packDisabledByRules = 'true';
    button.dataset.packDisabledLabel = label;
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    return;
  }

  if (button.dataset.packDisabledByRules === 'true') {
    button.disabled = button.dataset.packOriginalDisabled === 'true';
    button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
    delete button.dataset.packDisabledByRules;
    delete button.dataset.packDisabledLabel;
    delete button.dataset.packOriginalDisabled;
  }
}

function getPackProductStatusLabel(reason) {
  if (reason === 'selected') return 'Ya en tu pack';
  if (reason === 'maxed') return 'Limite alcanzado';
  if (reason === 'unavailable') return 'No disponible';
  return '';
}

function getPackProductImageLabel(reason) {
  return '';
}

function removePackProductStatus(card) {
  card.querySelectorAll('[data-pack-product-status="true"]').forEach((element) => element.remove());
}

function removePackProductImageState(card) {
  card.querySelectorAll('[data-pack-product-image-state="true"]').forEach((element) => element.remove());
}

function getPackStatusAnchor(card) {
  const titleLink = card.querySelector('[ref="productTitleLink"]');
  if (titleLink instanceof HTMLElement) return titleLink;

  const titleText = card.querySelector('.product-card-gallery__title-placeholder');
  if (titleText instanceof HTMLElement) return titleText;

  return null;
}

function getPackStatusParent(card) {
  const groupContent = card.querySelector('.group-block-content');
  if (groupContent instanceof HTMLElement) return groupContent;

  const cardContent = card.querySelector('.product-grid__card');
  if (cardContent instanceof HTMLElement) return cardContent;

  return card;
}

function upsertPackProductStatus(card, reason) {
  const label = getPackProductStatusLabel(reason);
  if (!label) {
    removePackProductStatus(card);
    return;
  }

  let status = card.querySelector('[data-pack-product-status="true"]');
  if (!(status instanceof HTMLElement)) {
    status = document.createElement('div');
    status.className = 'pack-product-status';
    status.dataset.packProductStatus = 'true';
  }

  status.dataset.packStatusReason = reason;
  status.textContent = label;

  const anchor = getPackStatusAnchor(card);
  if (anchor) {
    anchor.insertAdjacentElement('afterend', status);
    return;
  }

  getPackStatusParent(card).appendChild(status);
}

function upsertPackProductImageState(card, reason) {
  const gallery = card.querySelector('.card-gallery');
  if (!(gallery instanceof HTMLElement)) return;

  let state = Array.from(gallery.children).find((child) => child.getAttribute('data-pack-product-image-state') === 'true');
  if (!(state instanceof HTMLElement)) {
    state = document.createElement('span');
    state.className = 'pack-product-image-state';
    state.dataset.packProductImageState = 'true';
    state.setAttribute('aria-hidden', 'true');
    gallery.appendChild(state);
  }

  state.dataset.packImageStateReason = reason;
  state.textContent = '';

  const icon = document.createElement('span');
  icon.className = `pack-product-image-state__icon pack-product-image-state__icon--${reason}`;
  state.appendChild(icon);

  const label = getPackProductImageLabel(reason);
  if (label) {
    const labelElement = document.createElement('span');
    labelElement.className = 'pack-product-image-state__label';
    labelElement.textContent = label;
    state.appendChild(labelElement);
  }
}

function setProductCardPackVisualState(card, reason) {
  if (!reason) {
    delete card.dataset.packDisabledReason;
    removePackProductStatus(card);
    removePackProductImageState(card);
    return;
  }

  card.dataset.packDisabledReason = reason;
  upsertPackProductStatus(card, reason);
  upsertPackProductImageState(card, reason);
}

function restoreCategoryAvailability() {
  document.querySelectorAll('[data-pack-category-disabled="true"]').forEach((element) => {
    element.removeAttribute('aria-disabled');
    element.removeAttribute('data-pack-category-disabled');
    element.classList.remove('pack-category-disabled');

    if (element instanceof HTMLAnchorElement) {
      const originalTabIndex = element.dataset.packOriginalTabindex;
      if (originalTabIndex) {
        element.setAttribute('tabindex', originalTabIndex);
      } else {
        element.removeAttribute('tabindex');
      }
      delete element.dataset.packOriginalTabindex;
    }
  });

  document.querySelectorAll('[data-pack-category-disabled-input="true"]').forEach((element) => {
    if (!(element instanceof HTMLInputElement)) return;

    element.disabled = element.dataset.packOriginalDisabled === 'true';
    delete element.dataset.packCategoryDisabledInput;
    delete element.dataset.packOriginalDisabled;
  });
}

function setCategoryElementDisabled(element) {
  element.setAttribute('aria-disabled', 'true');
  element.setAttribute('data-pack-category-disabled', 'true');
  element.classList.add('pack-category-disabled');

  if (element instanceof HTMLAnchorElement) {
    if (!element.dataset.packOriginalTabindex) {
      element.dataset.packOriginalTabindex = element.getAttribute('tabindex') || '';
    }
    element.setAttribute('tabindex', '-1');
  }
}

function getFacetInputLabel(input) {
  if (!(input instanceof HTMLInputElement)) return '';

  const labelByFor = input.id ? document.querySelector(`label[for="${cssEscape(input.id)}"]`) : null;
  const directLabel = input.closest('label');

  return (
    input.dataset.label ||
    input.getAttribute('aria-label') ||
    labelByFor?.textContent ||
    directLabel?.textContent ||
    input.value ||
    ''
  );
}

function applyCategoryAvailability(maxedFurnitureLabels) {
  restoreCategoryAvailability();
  if (maxedFurnitureLabels.length === 0) return;

  document.querySelectorAll('a[href*="/collections/"]').forEach((anchor) => {
    if (!(anchor instanceof HTMLAnchorElement)) return;
    if (anchor.closest('product-card')) return;

    const handle = anchor.href.split('/collections/')[1]?.split(/[?#/]/)[0] || '';
    const label = `${anchor.textContent || ''} ${normalizeHandle(handle)}`;
    const shouldDisable = maxedFurnitureLabels.some((mueble) => textMatchesFurniture(label, mueble));

    if (shouldDisable) {
      setCategoryElementDisabled(anchor);
    }
  });

  document.querySelectorAll('.facets input[type="checkbox"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const label = getFacetInputLabel(input);
    const shouldDisable = maxedFurnitureLabels.some((mueble) => textMatchesFurniture(label, mueble));
    if (!shouldDisable) return;

    if (!input.dataset.packOriginalDisabled) {
      input.dataset.packOriginalDisabled = input.disabled ? 'true' : 'false';
    }
    input.disabled = true;
    input.dataset.packCategoryDisabledInput = 'true';

    const visualTarget =
      input.closest('.facets__inputs-list-item') ||
      (input.id ? document.querySelector(`label[for="${cssEscape(input.id)}"]`) : null);
    if (visualTarget instanceof HTMLElement) {
      setCategoryElementDisabled(visualTarget);
    }
  });
}

function applyProductCardAvailability(cartItems, config) {
  const selectedSkus = getSelectedSkuSet(cartItems);
  const maxedFurniture = getMaxedFurnitureSet(cartItems, config);
  const maxedFurnitureGroups = getMaxedFurnitureGroupSet(cartItems, config);
  const maxedFurnitureTypeGroups = getMaxedFurnitureTypeGroupSet(cartItems, config);

  document.querySelectorAll('product-card[data-product-sku]').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;

    const sku = normalizeSku(card.dataset.productSku);
    const product = config.productsBySku.get(sku);
    const muebleKey = mapSkuToFurniture(sku, config);
    const muebleGroupKey = mapSkuToFurnitureGroup(sku, config);
    const isSelectedProduct = selectedSkus.has(sku);
    const isUnavailableProduct = product?.available === false;
    const isMaxedFurniture =
      (muebleKey ? maxedFurniture.has(muebleKey) : false) ||
      (muebleGroupKey ? maxedFurnitureGroups.has(muebleGroupKey) : false) ||
      (muebleGroupKey ? maxedFurnitureTypeGroups.has(muebleGroupKey) : false);
    const disabled = isUnavailableProduct || isSelectedProduct || isMaxedFurniture;
    const disabledReason = isUnavailableProduct
      ? 'unavailable'
      : isSelectedProduct
        ? 'selected'
        : isMaxedFurniture
          ? 'maxed'
          : '';
    const label = disabledReason === 'unavailable'
      ? 'No disponible'
      : disabledReason === 'selected'
        ? 'Ya seleccionado'
        : 'Tope alcanzado';

    card.toggleAttribute('data-pack-disabled', disabled);
    card.classList.toggle('pack-product-disabled', disabled);
    card.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    setProductCardPackVisualState(card, disabled ? disabledReason : '');

    card.querySelectorAll('button[name="add"], .quick-add__button').forEach((button) => {
      setButtonDisabledByPackRules(button, disabled, label);
    });

    card.querySelectorAll('product-form-component').forEach((formComponent) => {
      if (formComponent instanceof HTMLElement) {
        formComponent.dataset.packBlocked = disabled ? 'true' : 'false';
        formComponent.dataset.packBlockedMessage = disabled ? label : '';
      }
    });
  });

  applyCategoryAvailability(getDisabledCategoryLabels(maxedFurniture, maxedFurnitureTypeGroups, config));

  document.querySelectorAll('product-form-component[data-product-sku]').forEach((formComponent) => {
    if (!(formComponent instanceof HTMLElement) || formComponent.closest('product-card')) return;

    const sku = normalizeSku(formComponent.dataset.productSku);
    const unavailable = config.productsBySku.get(sku)?.available === false;
    const label = unavailable ? 'No disponible' : '';
    formComponent.dataset.packBlocked = unavailable ? 'true' : 'false';
    formComponent.dataset.packBlockedMessage = label;
    formComponent.querySelectorAll('button[name="add"]').forEach((button) => {
      setButtonDisabledByPackRules(button, unavailable, label);
    });
  });
}

async function updatePackAvailabilityUi() {
  const requestId = ++latestAvailabilityRequestId;

  try {
    const [cart, config] = await Promise.all([fetchCartState(), getPackRulesConfig()]);
    if (requestId !== latestAvailabilityRequestId) return;

    applyProductCardAvailability(cart.items || [], config);
  } catch (error) {
    console.warn('[pack-rules] No se pudo actualizar el bloqueo visual del catálogo.', error);
  }
}

function getQuotePreviewElements() {
  return Array.from(document.querySelectorAll('[data-pack-quote-preview]')).flatMap((container) => {
    if (!(container instanceof HTMLElement)) return [];

    const amount = container.querySelector('[data-pack-quote-preview-amount]');
    if (!(amount instanceof HTMLElement)) return [];

    return [{ container, amount }];
  });
}

function renderPackQuotePreview(text, { visible = true, busy = false } = {}) {
  getQuotePreviewElements().forEach(({ container, amount }) => {
    const persistent = container.hasAttribute('data-pack-quote-preview-persistent');
    const shouldShow = visible || persistent;

    container.hidden = !shouldShow;
    container.setAttribute('aria-busy', busy ? 'true' : 'false');
    container.dataset.packQuoteRendered = 'true';
    amount.textContent = shouldShow ? text : '-';
  });
}

function normalizeQuoteAmount(payload) {
  if (!payload || typeof payload !== 'object') return null;

  const nestedQuote = typeof payload.quote === 'object' ? payload.quote : null;
  const nestedDataQuote = typeof payload.data?.quote === 'object' ? payload.data.quote : null;

  const directFields = [
    payload.valor_final,
    payload.valor_base,
    payload.valor_pack,
    payload.valor,
    payload.importe,
    payload.pack_value,
    payload.pack_total,
    payload.total,
    payload.amount,
    payload.quote,
    nestedQuote?.valor_pack,
    nestedQuote?.valor,
    nestedQuote?.importe,
    nestedQuote?.valor_final,
    nestedQuote?.valor_base,
    nestedQuote?.pack_value,
    nestedQuote?.pack_total,
    nestedQuote?.total,
    nestedQuote?.amount,
    payload.data?.valor_pack,
    payload.data?.valor,
    payload.data?.importe,
    payload.data?.valor_final,
    payload.data?.valor_base,
    payload.data?.pack_value,
    payload.data?.pack_total,
    payload.data?.total,
    payload.data?.amount,
    nestedDataQuote?.valor_pack,
    nestedDataQuote?.valor,
    nestedDataQuote?.importe,
    nestedDataQuote?.valor_final,
    nestedDataQuote?.valor_base,
    nestedDataQuote?.pack_value,
    nestedDataQuote?.pack_total,
    nestedDataQuote?.total,
    nestedDataQuote?.amount,
  ];

  for (const candidate of directFields) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
    if (typeof candidate === 'string') {
      const normalized = parseLocalizedNumber(candidate);
      if (Number.isFinite(normalized)) return normalized;
    }
  }

  return null;
}

function normalizeQuoteAmountFromText(source) {
  if (typeof source !== 'string' || source.trim() === '') return null;

  const currencyMatches = source.match(/(?:ARS|\$)\s*[\d.]+(?:,\d+)?/gi) || [];
  for (const chunk of currencyMatches) {
    const normalized = parseLocalizedNumber(chunk);
    if (Number.isFinite(normalized) && normalized > 0) {
      return normalized;
    }
  }

  return null;
}

function parseLocalizedNumber(value) {
  const cleaned = String(value || '').replace(/[^\d,.-]/g, '');
  if (!cleaned) return Number.NaN;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  if (hasComma && hasDot) {
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    if (lastComma > lastDot) {
      return Number(cleaned.replace(/\./g, '').replace(',', '.'));
    }

    return Number(cleaned.replace(/,/g, ''));
  }

  if (hasComma) {
    if (/,\d{1,2}$/.test(cleaned)) {
      return Number(cleaned.replace(',', '.'));
    }
    return Number(cleaned.replace(/,/g, ''));
  }

  if (hasDot) {
    if (/\.\d{1,2}$/.test(cleaned)) {
      return Number(cleaned);
    }
    return Number(cleaned.replace(/\./g, ''));
  }

  return Number(cleaned);
}

async function fetchPackQuoteAmount(items) {
  const key = JSON.stringify(items);
  if (quoteRequests.has(key)) return quoteRequests.get(key);
  const request = loadPackQuoteAmount(items).finally(() => quoteRequests.delete(key));
  quoteRequests.set(key, request);
  return request;
}

function cacheQuote(key, amount) {
  quoteCache.set(key, { amount, expires: Date.now() + 30000 });
  while (quoteCache.size > 50) quoteCache.delete(quoteCache.keys().next().value);
}

async function loadPackQuoteAmount(items) {
  const cacheKey = JSON.stringify(items);
  const cached = quoteCache.get(cacheKey);
  if (cached && cached.expires > Date.now()) return cached.amount;

  const payload = {
    items,
    subscription_type: DEFAULT_SUBSCRIPTION_TYPE,
  };

  let lastError = null;

  for (const endpoint of PACK_QUOTE_ENDPOINTS) {
    try {
      const response = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        lastError = new Error('No se pudo obtener la cotización. Intentá nuevamente en unos momentos.');
        if (![404, 405].includes(response.status)) throw lastError;
        continue;
      }

      const responseData = await response.json();
      const amount = normalizeQuoteAmount(responseData);
      if (amount === null) {
        throw new Error('La cotización recibida es inválida.');
      }

      cacheQuote(cacheKey, amount);
      return amount;
    } catch (error) {
      // A timeout or overload is not a missing legacy endpoint.
      throw error;
    }
  }

  try {
    const checkoutPreviewUrl = buildCheckoutUrl(items);
    const checkoutResponse = await fetchWithTimeout(checkoutPreviewUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json, text/html',
      },
    });

    if (checkoutResponse.ok) {
      const responseContentType = checkoutResponse.headers.get('content-type') || '';

      if (responseContentType.includes('application/json')) {
        const responseData = await checkoutResponse.json();
        const amount = normalizeQuoteAmount(responseData);
        if (amount !== null) {
          cacheQuote(cacheKey, amount);
          return amount;
        }
      } else {
        const responseText = await checkoutResponse.text();
        const amount = normalizeQuoteAmountFromText(responseText);
        if (amount !== null) {
          cacheQuote(cacheKey, amount);
          return amount;
        }
      }
    }
  } catch (error) {
    lastError = error;
  }

  throw lastError || new Error('No fue posible obtener el valor del pack.');
}

function formatCurrency(value) {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

async function updatePackQuotePreview() {
  const requestId = ++latestPreviewRequestId;
  if (getQuotePreviewElements().length === 0) return;

  renderPackQuotePreview(formatCurrency(0), { visible: false });

  try {
    const [cart, config] = await Promise.all([fetchCartState(), getPackRulesConfig()]);
    if (requestId !== latestPreviewRequestId) return;

    const sortedCartItems = sortCartItemsBySelectionOrder(cart.items || [], config);
    const items = sortedCartItems.map((item) => ({
      sku: (item.sku || '').trim(),
      qty: item.quantity,
    }));

    if (items.length === 0) return;

    const validation = validatePackSelection(items, config);
    if (!validation.valid) {
      renderPackQuotePreview('Sin calcular', { visible: false });
      return;
    }

    renderPackQuotePreview(QUOTE_LOADING_TEXT, { busy: true });

    const quoteValue = await fetchPackQuoteAmount(items);
    if (requestId !== latestPreviewRequestId) return;
    renderPackQuotePreview(formatCurrency(quoteValue));
  } catch (error) {
    if (requestId !== latestPreviewRequestId) return;
    renderPackQuotePreview('No disponible', { visible: false });
    console.warn('[pack-quote] No se pudo actualizar la vista previa del valor del pack.', error);
  }
}

async function handlePackQuoteClick(button) {
  if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
  if (button.dataset.packQuoteProcessing === 'true') return;

  const error = getErrorElement(button);
  let accountProfile = null;

  try {
    accountProfile = await getCheckoutAccountProfile(button);
  } catch (errorObj) {
    if (error) {
      error.textContent = errorObj instanceof Error ? errorObj.message : 'No se pudo validar la cuenta.';
    }
    setAccountButtonLabel(button);
    return;
  }

  if (!accountProfile || !isAccountProfileComplete(accountProfile)) {
    trackCheckoutEvent('login_required_shown', {
      metadata: { result: accountProfile ? 'incomplete_profile' : 'signed_out' },
    });
    redirectToAccount(button);
    return;
  }

  const originalLabel = button.textContent;

  button.dataset.packQuoteProcessing = 'true';
  button.disabled = true;
  button.textContent = 'Calculando...';
  if (error) error.textContent = '';

  try {
    const items = await fetchCartItems();
    const { validation } = await validateCartWithPackRules(items);
    if (!validation.valid) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('cart__checkout-button--pack-disabled');
      button.dataset.packQuoteProcessing = 'false';
      button.textContent = originalLabel;
      if (error) error.textContent = '';
      return;
    }

    const selection = await getSystemSelection({ forceRefresh: true, allowLegacyMigration: false });
    if (!selection?.selection_id || selection.status !== 'ACTIVA') {
      throw new Error('No fue posible validar Mi selección. Volvé a intentarlo.');
    }
    trackCheckoutEvent('begin_checkout', {
      selection_id: selection.selection_id,
      metadata: { item_count: items.reduce((total, item) => total + Number(item.qty || 0), 0) },
    });
    const checkoutUrl = buildCheckoutUrl(items, accountProfile, selection.selection_id);
    await closeCartDrawerIfOpen();
    openPackCheckoutModal(checkoutUrl);

    button.dataset.packQuoteProcessing = 'false';
    button.disabled = false;
    button.textContent = originalLabel;
  } catch (errorObj) {
    if (error) {
      error.textContent = errorObj instanceof Error ? errorObj.message : 'No fue posible iniciar el checkout del pack.';
    }

    button.dataset.packQuoteProcessing = 'false';
    button.disabled = false;
    button.textContent = originalLabel;
  }
}

function onPackQuoteButtonClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-pack-quote-button]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  event.stopPropagation();
  handlePackQuoteClick(button);
}

if (!window.__packQuoteClickBound) {
  window.__packQuoteClickBound = true;
  document.addEventListener('click', onPackQuoteButtonClick);
}

if (!window.__packQuoteAutoUpdateBound) {
  window.__packQuoteAutoUpdateBound = true;
  const refreshPageAccountAndQuoteUi = () => {
    refreshAccountAwareCheckoutButtons();
    updatePackQuotePreview();
  };
  refreshAccountAwareCheckoutButtons();
  setTimeout(refreshAccountAwareCheckoutButtons, 250);
  watchCheckoutButtonMounts();
  window.addEventListener(DECO_ACCOUNT_CHANGED_EVENT, refreshAccountAwareCheckoutButtons);
  window.addEventListener('pageshow', refreshPageAccountAndQuoteUi);
  document.addEventListener('shopify:section:load', refreshPageAccountAndQuoteUi);
  window.addEventListener('storage', (event) => {
    if (Object.values(DECO_ACCOUNT_SESSION_KEYS).includes(event.key)) {
      refreshAccountAwareCheckoutButtons();
    }
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAccountAwareCheckoutButtons, { once: true });
  }
  window.addEventListener('message', handlePackCheckoutMessage);
  window.addEventListener('deco-account:after-complete', handleDecoAccountAfterComplete);
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    updatePackQuotePreview();
    updatePackAvailabilityUi();
    refreshAccountAwareCheckoutButtons();
    setTimeout(refreshAccountAwareCheckoutButtons, 75);
    setTimeout(refreshAccountAwareCheckoutButtons, 350);
  });
  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    setTimeout(updatePackAvailabilityUi, 250);
    setTimeout(updatePackAvailabilityUi, 1000);
  });
  document.addEventListener('pack-product-card:update', () => {
    updatePackAvailabilityUi();
  });
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const blockedCategory = target.closest('[data-pack-category-disabled="true"]');
      if (!blockedCategory) return;

      event.preventDefault();
      event.stopPropagation();
    },
    true
  );
}

createPackCheckoutModal();
updatePackQuotePreview();
updatePackAvailabilityUi();
getPackRulesConfig().catch((error) => {
  console.warn('[pack-rules] No se pudieron precargar las reglas en Shopify.', error);
});
